import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

type Props = {
  featureName: string;
  /** Extra context logged alongside the error (props/state snapshot). */
  context?: Record<string, unknown>;
  children: ReactNode;
  fallback?: ReactNode;
};

type State = { error: Error | null };

/**
 * Feature-level error boundary. Prevents a crash inside one feature
 * (e.g. the CV Builder or the Motivational Letter dialog) from
 * blanking the whole page. Logs the error + serialisable context to
 * the console so the failing props/state are visible in the runtime
 * error snapshot.
 */
export class FeatureErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Safe stringify — never let logging itself throw.
    let ctx: unknown = this.props.context ?? {};
    try {
      ctx = JSON.parse(
        JSON.stringify(this.props.context ?? {}, (_k, v) => {
          if (v instanceof File)
            return { __file: true, name: v.name, size: v.size, type: v.type };
          if (v instanceof Blob) return { __blob: true, size: v.size, type: v.type };
          return v;
        }),
      );
    } catch {
      /* ignore serialisation failures */
    }
    // eslint-disable-next-line no-console
    console.error(
      `[FeatureErrorBoundary:${this.props.featureName}] crash`,
      {
        message: error?.message,
        name: error?.name,
        stack: error?.stack,
        componentStack: info?.componentStack,
        context: ctx,
      },
    );
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    return (
      <div
        role="alert"
        className="rounded-lg border border-destructive/40 bg-destructive/5 p-5 text-sm"
      >
        <div className="flex items-center gap-2 font-semibold text-destructive">
          <AlertTriangle className="h-4 w-4" />
          Something went wrong in {this.props.featureName}.
        </div>
        <p className="mt-2 text-foreground/70">
          The rest of the page is fine — you can retry this section. If the
          problem keeps happening, please refresh the page.
        </p>
        <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-background/60 p-2 text-[11px] text-foreground/60">
          {this.state.error.message || String(this.state.error)}
        </pre>
        <button
          type="button"
          onClick={this.reset}
          className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-navy bg-background px-3 py-1.5 text-xs font-medium text-navy hover:bg-navy/5"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Try again
        </button>
      </div>
    );
  }
}
